import React, { useEffect, useState, useMemo } from "react";
import { Form, Button, Badge, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import RecipientCard, { CardGrid } from "../shared/RecipientCard";
import RemoveBadConfirmDialog from "./RemoveBadConfirmDialog";

const GROUP_MODE_STORAGE_KEY = "recipientSummary.groupBySource";

// Filter chip values. Names mirror the verdict enum + an explicit
// "unvetted" bucket (no verdict yet) + "all" (no filter). View
// operation only — does NOT scope what gets sent at submit time.
const VERDICT_FILTERS = ["all", "good", "ok", "bad", "unvetted"];

// Counts the pool's verdict distribution for the inline summary text.
// Always counted over the FULL pool — filter chips narrow what's
// visible, not what's counted.
function tallyVerdicts(pool, verdicts) {
  const counts = { good: 0, ok: 0, bad: 0, unvetted: 0 };
  pool.forEach((c) => {
    const v = verdicts[c.id]?.verdict;
    if (v === "good" || v === "ok" || v === "bad") {
      counts[v] += 1;
    } else {
      counts.unvetted += 1;
    }
  });
  return counts;
}

function passesVerdictFilter(candidate, verdicts, filter) {
  if (filter === "all") return true;
  const v = verdicts[candidate.id]?.verdict;
  if (filter === "unvetted") return !v;
  return v === filter;
}

function loadInitialGroupBySource() {
  if (typeof window === "undefined" || !window.localStorage) return true;
  try {
    const stored = window.localStorage.getItem(GROUP_MODE_STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
    return true;
  } catch (_) {
    return true;
  }
}

// Inline recipient list on StoryIdeaForm. Card-grid layout:
//   - Each candidate renders as a rounded-rect card (avatar, name,
//     title/workplace, bio snippet with read-more, checkbox).
//   - Cards in a responsive CSS grid (auto-fill at minmax(220px, 1fr));
//     ~5 across at typical laptop width with the recipient section now
//     spanning the full page.
//   - Grouped by source when there are multiple search sources;
//     single-source pool renders flat (one grid, no group header).
//   - Group sections stay collapsible; per-group select-all and global
//     select-all in the toolbar; bulk Remove for selected rows.
//
// The "Pick recipients" button opens the modal in the parent
// (onOpenPicker). The picker adds rows; this surface is where the
// admin curates / prunes them.
function sourceLabel(source) {
  if (!source) return "Recipient";
  if (source.type === "semantic" && source.params?.query) {
    return `Smart match: "${source.params.query}"`;
  }
  if (source.type === "by_name" && source.params?.query) {
    return `By name: "${source.params.query}"`;
  }
  if (source.type === "by_outlet") {
    const outlets = source.params?.outlet_names;
    if (Array.isArray(outlets) && outlets.length > 0) {
      return `Outlets: ${outlets.join(", ")}`;
    }
    if (source.params?.outlet_name) return `Outlet: ${source.params.outlet_name}`;
    if (source.params?.query) return `By outlet: "${source.params.query}"`;
    return "By outlet";
  }
  if (source.type === "previously_saved") return "Previously saved";
  return source.type || "Recipient";
}

function rebuildFormData(remainingPool) {
  const ids = remainingPool.map((c) => c.id);
  const groupsByKey = new Map();
  remainingPool.forEach((c) => {
    const key = JSON.stringify(c.source || {});
    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, {
        source_type: c.source?.type || "unknown",
        source_params: c.source?.params || {},
        user_ids: [],
      });
    }
    groupsByKey.get(key).user_ids.push(c.id);
  });
  return { ids, groups: Array.from(groupsByKey.values()), candidates: remainingPool };
}

export default function RecipientSummary({
  pool,
  onOpenPicker,
  onChange,
  error,
  // AI vet display only. `verdicts` is the
  // {uuid: {verdict, reason, model, vetted_at}} hash from formik, used
  // here to decorate cards + drive filter chips + drive the Remove-all-bad
  // dialog. We deliberately don't take an `onVerdictsChange` callback:
  // Remove-all-bad mutates the recipient pool via `onChange`, not the
  // verdict hash — orphaned verdicts for removed recipients are harmless
  // (derivations work over the current pool) and re-usable if the same
  // recipient is later re-added. The "Vet visible" action lives inside the
  // picker modal (VetToolbar), not here.
  verdicts = {},
  // Picker-entry gate (set by the parent form). When `disabled`, the
  // "Pick recipients" button is disabled and `lockedHint` explains why —
  // shown whenever disabled, and associated with the button via
  // aria-describedby so the reason is announced (a disabled button isn't
  // focusable and a bare title= tooltip is invisible to SR/keyboard users).
  // The existing-pool list still renders, so a saved draft with a cleared
  // title still shows its recipients — only adding is blocked.
  disabled = false,
  lockedHint,
}) {
  const [checked, setChecked] = useState(() => new Set());
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [groupBySource, setGroupBySource] = useState(loadInitialGroupBySource);
  // Verdict filter chip selection. View operation only — does NOT scope
  // what gets sent (send always reads the full pool). Reset implicitly
  // any time the pool changes substantially via reopens; we keep it
  // sticky within a session for the common workflow of "filter to bad,
  // remove all, then look at not-vetted next."
  const [verdictFilter, setVerdictFilter] = useState("all");
  useEffect(() => {
    try {
      window.localStorage?.setItem(GROUP_MODE_STORAGE_KEY, String(groupBySource));
    } catch (_) {
      // localStorage unavailable — keep in-memory state.
    }
  }, [groupBySource]);

  const groups = useMemo(() => {
    const byKey = new Map();
    pool.forEach((c) => {
      const key = JSON.stringify(c.source || {});
      if (!byKey.has(key)) {
        byKey.set(key, { key, source: c.source, candidates: [] });
      }
      byKey.get(key).candidates.push(c);
    });
    return Array.from(byKey.values());
  }, [pool]);

  const count = pool.length;
  const allChecked = count > 0 && pool.every((c) => checked.has(c.id));
  const someChecked = checked.size > 0 && !allChecked;

  // Verdict distribution + filter machinery. verdictCounts
  // always reflects the FULL pool; filter chips narrow what's visible
  // via filteredPool. Send-time logic always reads `pool`, never
  // `filteredPool` — filter is a view-op only.
  const verdictCounts = useMemo(() => tallyVerdicts(pool, verdicts), [pool, verdicts]);
  const hasAnyVerdicts = verdictCounts.good + verdictCounts.ok + verdictCounts.bad > 0;
  const filteredPool = useMemo(
    () => (verdictFilter === "all" ? pool : pool.filter((c) => passesVerdictFilter(c, verdicts, verdictFilter))),
    [pool, verdicts, verdictFilter],
  );
  // Subset for the "Remove all bad" confirmation dialog. Dialog state
  // is internal to RecipientSummary; flushes through onChange on
  // confirm just like the row-level remove path.
  const badCandidates = useMemo(() => pool.filter((c) => verdicts[c.id]?.verdict === "bad"), [pool, verdicts]);
  const [showRemoveBad, setShowRemoveBad] = useState(false);

  const handleConfirmRemoveBad = (idsToRemove) => {
    if (idsToRemove.length === 0) return;
    const toRemoveSet = new Set(idsToRemove);
    const remaining = pool.filter((c) => !toRemoveSet.has(c.id));
    onChange(rebuildFormData(remaining));
  };

  const toggleRow = (id) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setManyChecked = (ids, isChecked) =>
    setChecked((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (isChecked) next.add(id);
        else next.delete(id);
      });
      return next;
    });

  const toggleCollapse = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const removeChecked = () => {
    if (checked.size === 0) return;
    const remaining = pool.filter((c) => !checked.has(c.id));
    setChecked(new Set());
    onChange(rebuildFormData(remaining));
  };

  return (
    <div className="mb-3">
      <div className="mb-3 d-flex gap-2 flex-wrap align-items-center">
        <Button
          size="sm"
          variant="outline-primary"
          onClick={onOpenPicker}
          disabled={disabled}
          aria-describedby={disabled && lockedHint ? "recipient-picker-locked-hint" : undefined}
        >
          <i className="fa-solid fa-user-plus me-1" aria-hidden="true" />
          {count > 0 ? "Add more recipients…" : "Pick recipients…"}
        </Button>
        {disabled && lockedHint && (
          <span id="recipient-picker-locked-hint" className="small text-muted">
            {lockedHint}
          </span>
        )}
      </div>

      {count > 0 && (
        <div className="border rounded">
          <div
            className="d-flex align-items-center gap-2 p-2 border-bottom"
            style={{ backgroundColor: "rgb(53 98 135 / 4%)" }}
          >
            <Form.Check
              type="checkbox"
              checked={allChecked}
              ref={(el) => {
                // HTMLInputElement.indeterminate has no React prop equivalent — must set on the ref.
                // eslint-disable-next-line no-param-reassign
                if (el) el.indeterminate = someChecked;
              }}
              onChange={(e) =>
                setManyChecked(
                  pool.map((c) => c.id),
                  e.target.checked,
                )
              }
              aria-label={allChecked ? "Deselect all recipients" : "Select all recipients"}
            />
            <span className="small text-muted">
              {checked.size > 0 ? `${checked.size} selected` : `${count} recipient${count === 1 ? "" : "s"}`}
            </span>
            {hasAnyVerdicts && (
              <span className="small text-muted ms-2 d-none d-md-inline" aria-label="Verdict distribution">
                · {verdictCounts.good} good · {verdictCounts.ok} ok · {verdictCounts.bad} bad · {verdictCounts.unvetted}{" "}
                not vetted
              </span>
            )}
            <div className="flex-grow-1" />
            {hasAnyVerdicts && (
              <ToggleButtonGroup
                type="radio"
                name="recipient-summary-verdict-filter"
                size="sm"
                value={verdictFilter}
                onChange={setVerdictFilter}
                aria-label="Filter by AI verdict"
              >
                {VERDICT_FILTERS.map((f) => (
                  <ToggleButton
                    key={f}
                    id={`verdict-filter-${f}`}
                    value={f}
                    variant="outline-secondary"
                    style={{ textTransform: "capitalize" }}
                  >
                    {f === "unvetted" ? "Not vetted" : f}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
            {/* Grouping toggle only makes sense when 2+ sources contributed;
                otherwise the result is identical either way. */}
            {groups.length > 1 && (
              <Form.Check
                type="switch"
                id="recipient-summary-group-by-source"
                checked={groupBySource}
                onChange={(e) => setGroupBySource(e.target.checked)}
                label={<span className="small">Group by search</span>}
              />
            )}
            {checked.size > 0 && (
              <Button size="sm" variant="outline-danger" onClick={removeChecked}>
                Remove {checked.size} selected
              </Button>
            )}
            {badCandidates.length > 0 && (
              <Button size="sm" variant="outline-danger" onClick={() => setShowRemoveBad(true)}>
                Remove {badCandidates.length} bad
              </Button>
            )}
          </div>

          {groups.length === 1 || !groupBySource ? (
            // Either only one source contributed (grouping would be a
            // single section, pointless) OR the admin has turned grouping
            // off — render one flat grid in the pool's natural order
            // (which preserves the add-order across searches). filteredPool
            // narrows by the active verdict-filter chip; verdictFilter="all"
            // makes filteredPool === pool.
            <CardGrid>
              {filteredPool.map((c) => (
                <RecipientCard
                  key={c.id}
                  candidate={c}
                  checked={checked.has(c.id)}
                  onToggle={() => toggleRow(c.id)}
                  verdict={verdicts[c.id]}
                />
              ))}
            </CardGrid>
          ) : (
            groups.map(({ key, source, candidates }) => {
              // Filter the group's candidates by the active verdict-filter chip.
              const visibleCandidates =
                verdictFilter === "all"
                  ? candidates
                  : candidates.filter((c) => passesVerdictFilter(c, verdicts, verdictFilter));
              // Hide the group section entirely if the filter empties it —
              // keeps the visual surface tight when chips narrow aggressively.
              if (visibleCandidates.length === 0) return null;
              const isCollapsed = collapsed.has(key);
              const groupIds = candidates.map((c) => c.id);
              const groupAllChecked = groupIds.every((id) => checked.has(id));
              const groupSomeChecked = !groupAllChecked && groupIds.some((id) => checked.has(id));

              return (
                <div key={key} className="border-bottom">
                  {/* The whole row toggles collapse on click/Enter/Space.
                      The checkbox stops propagation so toggling selection
                      doesn't also toggle the section open/closed. */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleCollapse(key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleCollapse(key);
                      }
                    }}
                    className="d-flex align-items-center gap-2 px-2 py-2 bg-white"
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={groupAllChecked}
                      ref={(el) => {
                        // HTMLInputElement.indeterminate has no React prop equivalent — must set on the ref.
                        // eslint-disable-next-line no-param-reassign
                        if (el) el.indeterminate = groupSomeChecked;
                      }}
                      onChange={(e) => setManyChecked(groupIds, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select all in ${sourceLabel(source)}`}
                    />
                    <span aria-hidden="true" style={{ width: 16 }}>
                      {isCollapsed ? "▶" : "▼"}
                    </span>
                    <span className="fw-semibold flex-grow-1 text-truncate" title={sourceLabel(source)}>
                      {sourceLabel(source)}
                    </span>
                    <Badge bg="secondary">{candidates.length}</Badge>
                  </div>
                  {!isCollapsed && (
                    <CardGrid>
                      {visibleCandidates.map((c) => (
                        <RecipientCard
                          key={c.id}
                          candidate={c}
                          checked={checked.has(c.id)}
                          onToggle={() => toggleRow(c.id)}
                          verdict={verdicts[c.id]}
                        />
                      ))}
                    </CardGrid>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {error && <div className="invalid-feedback d-block">{error}</div>}

      <RemoveBadConfirmDialog
        show={showRemoveBad}
        onClose={() => setShowRemoveBad(false)}
        onConfirm={handleConfirmRemoveBad}
        candidates={badCandidates}
        verdicts={verdicts}
      />
    </div>
  );
}
